import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  addOnOfferFor,
  limitUpsellOffer,
  type AddOnOffer,
  type LimitShortfall,
  type LimitUpsellOffer,
} from 'fym-shared/src/music24/limitUpsell';
import type { Music24PlanTier } from 'fym-shared/src/music24/music24Plans';
import { fymAPI } from '../../../redux/api/fymAPI';
import { useLimitUpsellAddOns } from './useLimitUpsellAddOns';

export type LimitUpsellAddState = 'idle' | 'confirm' | 'adding' | 'added' | 'failed';

export interface LimitUpsellState {
  /** Null while `billing.monthlyAddOns` loads: the card shows the numbers and no button yet. */
  offer: LimitUpsellOffer | null;
  /** The add-on of the click; null for every offer but `addOn`. */
  addOn: AddOnOffer | null;
  plan: Music24PlanTier | null;
  addState: LimitUpsellAddState;
  /** What the last click added (for the green line); null before. */
  added: { count: number; kind: LimitShortfall['kind'] } | null;
  openConfirm: () => void;
  cancel: () => void;
  confirm: () => void;
}

/**
 * The offer of a limit upsell card (design limit-upsell a1-a10). It reads `billing.monthlyAddOns`
 * (every member may read it) and picks the offer by its availability. The add-on of the owner of a
 * web plan is the shortfall rounded up to the slider step; the click sets it with
 * `billing.setMonthlyAddOns` (the current quantities plus the extra units), then reads the posts
 * and the credits left again, so the flow goes on with the new numbers.
 */
export function useLimitUpsell(shortfall: LimitShortfall | null, onAdded?: () => void): LimitUpsellState {
  const dispatch = useDispatch();
  const { data, isLoading, save } = useLimitUpsellAddOns();
  const [addState, setAddState] = useState<LimitUpsellAddState>('idle');
  const [added, setAdded] = useState<LimitUpsellState['added']>(null);

  const addOn = useMemo(
    () =>
      shortfall === null || data === undefined || data.availability !== 'available'
        ? null
        : addOnOfferFor({
            shortfall,
            current: data.current,
            interval: data.interval,
            planPeriodCents: data.planPeriodCents,
          }),
    [data, shortfall]
  );

  let offer: LimitUpsellOffer | null = isLoading ? null : limitUpsellOffer(data?.availability);
  if (offer === 'addOn' && addOn === null) {
    // The add-on is at the slider end: a higher plan is the next step.
    offer = 'higherPlan';
  }

  const openConfirm = useCallback(() => setAddState('confirm'), []);
  const cancel = useCallback(() => setAddState('idle'), []);
  const confirm = useCallback(() => {
    if (addOn === null) {
      return;
    }
    setAddState('adding');
    save.mutate(addOn.next, {
      onSuccess: () => {
        setAdded({ count: addOn.add, kind: addOn.kind });
        setAddState('added');
        // The posts and the credits left change at once: every flow reads them again.
        dispatch(fymAPI.util.invalidateTags(['PromoQuota', 'ReelsPlanPreview', 'VideoCredits']));
        onAdded?.();
      },
      onError: (error) => {
        console.error(error);
        setAddState('failed');
      },
    });
  }, [addOn, dispatch, onAdded, save]);

  return { offer, addOn, plan: data?.plan ?? null, addState, added, openConfirm, cancel, confirm };
}
